import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, map, mergeMap, Observable, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Board, Card } from '../models/board.model';
import { User } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class BoardService {
  public boards$: ReplaySubject<Board[]> = new ReplaySubject<Board[]>(0);
  public boardsObject$: ReplaySubject<{ [id: string]: Board }> = new ReplaySubject<{ [id: string]: Board }>(0);

  public boards: Board[] = [];
  public boardsObject: { [id: string]: Board } = {};

  // Private
  private _board: BehaviorSubject<Board | null>;
  private _boards: BehaviorSubject<Board[] | null>;
  private _card: BehaviorSubject<Card | null>;

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private firestore$: AngularFirestore
  ) {
    // Set the private defaults
    this._board = new BehaviorSubject(null);
    this._boards = new BehaviorSubject(null);
    this._card = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for board
   */
  get board$(): Observable<Board> {
    return this._board.asObservable();
  }

  /**
   * Getter for card
   */
  get card$(): Observable<Card> {
    return this._card.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  getAll(): Observable<Board[]> {
    return this.firestore$.collection<Board>('boards').valueChanges({ idField: 'id' })
      .pipe(
        tap((boards) => {
          const obBoards = {};
          boards.forEach(fe => {
            obBoards[fe.id] = fe;
          });
          this.boardsObject = obBoards;
          this.boards = boards;

          this.boardsObject$.next(obBoards);
          this.boards$.next(boards);
        })
      );
  }

  /**
   * Get boards
   */
  getBoards(): Observable<Board[]> {
    return this.firestore$.collection<Board>('boards')
      .valueChanges()
      .pipe(take(1))
      .pipe(
        mergeMap(boards => new Observable<Board[]>(subscriber => {
            this.firestore$.collection<User>('users').valueChanges().subscribe(users => {
              boards.forEach(board => {
                board.membersData = board.members.map(member => users.find(u => u.id === member)).filter(f => !!f);
              });
              subscriber.next(boards);
            });
          })
        )
      )
      .pipe(
        tap(boards => this._boards.next(boards))
      );
  }

  /**
   * Get board
   *
   * @param id
   */
  getBoard(id: string): Observable<Board> {
    return this.firestore$.doc<Board>('boards/' + id).valueChanges().pipe(take(1)).pipe(
      tap(board => this._board.next(board))
    );
  }

  /**
   * Create board
   *
   * @param board
   */
  createBoard(board: Board): Observable<Board> {
    const id = this.firestore$.createId();
    board.id = id;
    const observable$: Observable<any> = new Observable(subscriber => {
      this.firestore$.collection<Board>('boards').doc(id).set(board)
        .then(() => {
          subscriber.next(board);
          subscriber.complete();
        });
    });
    return this.boards$.pipe(
      take(1),
      switchMap(boards => observable$.pipe(
        map((newBoard) => {
          // Update the boards with the new board
          this._boards.next([ ...boards, newBoard ]);

          // Return new board from observable
          return newBoard;
        })
      )),
      switchMap(newBoard => this.firestore$.collection<User>('users').valueChanges().pipe(
        take(1),
        map(users => {
          newBoard.membersData = newBoard.members.map(member => users.find(u => u.id === member)).filter(f => !!f);
          return newBoard;
        })
      ))
    );
  }

  /**
   * Update the board
   *
   * @param board
   */
  updateBoard(board: Board): Observable<Board> {
    delete board.membersData;
    board.lastActivity = new Date();
    const observable$: Observable<any> = new Observable(subscriber => {
      this.firestore$.collection<Board>('boards').doc(board.id).update(board)
        .then(() => {
          subscriber.next(board);
          subscriber.complete();
        });
    });
    return this.boards$.pipe(
      take(1),
      switchMap(boards => observable$),
      switchMap(newBoard => this.firestore$.collection<User>('users').valueChanges().pipe(
        take(1),
        map(users => {
          newBoard.membersData = newBoard.members.map(member => users.find(u => u.id === member)).filter(f => !!f);
          return newBoard;
        })
      ))
    );
  }

  /**
   * Delete the board
   *
   * @param board
   */
  deleteBoard(board: Board): Observable<boolean> {
    const observable$: Observable<any> = new Observable(subscriber => {
      this.firestore$.collection<Board>('boards').doc(board.id).delete()
        .then(() => {
          subscriber.next(board);
          subscriber.complete();
        });
    });
    return this.boards$.pipe(
      take(1),
      switchMap(boards => observable$.pipe(
        map(() => {

          // Find the index of the deleted board
          const index = boards.findIndex(item => item.id === board.id);

          // Delete the board
          boards.splice(index, 1);

          // Update the boards
          this._boards.next(boards);

          // Update the board
          this._board.next(null);

          // Update the card
          this._card.next(null);

          // Return the deleted status
          return true;
        })
      ))
    );
  }
}
